import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { BookText, PiggyBank, Building2, LogOut, ArrowRight, ArrowLeft, ReceiptText, BarChart3 } from "lucide-react";

const Card = ({ icon: Icon, title, desc, onClick, testid, accent }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="group text-left rounded-2xl border border-border bg-card p-8 shadow-sm hover:shadow-md hover:border-primary transition-all duration-200 active:scale-[0.99]"
  >
    <div className={`h-14 w-14 rounded-xl flex items-center justify-center ${accent}`}>
      <Icon className="h-7 w-7 text-white" />
    </div>
    <h2 className="font-heading text-2xl font-semibold tracking-tight mt-6">{title}</h2>
    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{desc}</p>
    <div className="flex items-center gap-1.5 text-sm font-medium text-primary mt-6 group-hover:gap-2.5 transition-all">
      Openen <ArrowRight className="h-4 w-4" />
    </div>
  </button>
);

export default function BoekhoudingHub() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background" data-testid="boekhouding-hub">
      <header className="border-b border-border bg-card">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-md bg-primary flex items-center justify-center text-primary-foreground">
              <Building2 className="h-5 w-5" />
            </div>
            <div className="leading-tight">
              <div className="font-heading font-semibold text-sm">Singh Express Shop BV</div>
              <div className="text-xs text-muted-foreground">BE 0788.952.072</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground hidden sm:block">{user?.name}</span>
            <Button variant="ghost" size="sm" data-testid="hub-logout" onClick={() => { logout(); navigate("/"); }}>
              <LogOut className="h-4 w-4 mr-2" /> Afmelden
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-16">
        <Button variant="ghost" size="sm" className="mb-6 -ml-2" data-testid="back-to-modules" onClick={() => navigate("/hub")}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Modules
        </Button>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Boekhouding</h1>
        <p className="text-muted-foreground mt-2">Kies een onderdeel.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
          <Card
            testid="boek-dagontvangsten"
            icon={BookText}
            accent="bg-primary"
            title="Dagontvangsten"
            desc="Digitaal dagboek van ontvangsten, Z-tickets, BTW-rapporten, centralisatie en audit trail."
            onClick={() => navigate("/dashboard")}
          />
          <Card
            testid="boek-kasboek"
            icon={PiggyBank}
            accent="bg-amber-600"
            title="Kasboek"
            desc="Centraal kasboek: kasontvangsten en -uitgaven registreren met beginsaldo, saldo-opvolging en export."
            onClick={() => navigate("/kasboek")}
          />
          <Card
            testid="boek-factuurboek"
            icon={ReceiptText}
            accent="bg-indigo-600"
            title="Factuurboek"
            desc="Aankoopfacturen in bulk uploaden, automatisch uitlezen, leveranciersprofielen en btw-overzicht per categorie."
            onClick={() => navigate("/factuurboek")}
          />
          <Card
            testid="boek-rapporten"
            icon={BarChart3}
            accent="bg-emerald-600"
            title="Rapporten"
            desc="Opbrengsten uit het dagboek, diverse opbrengsten (pakjesdiensten) en alle aankoopoverzichten (aankopen, autokosten, energie)."
            onClick={() => navigate("/rapporten")}
          />
        </div>
      </main>
    </div>
  );
}
